/**
 * Segmented SKU anatomy (Phase 65 / BLU-594 PR2).
 *
 * The mono "shape" used both in tier-1 ("Every variant becomes") and the Code
 * tables hero ("What these tables build"). Each segment is a labelled chip;
 * `highlight` lights the owning segment when the operator hovers a mapping card
 * (state-lifted, 200ms per DESIGN §5, motion-reduce safe). Dynamic segments
 * (size/colour, which vary per variant) carry the single brand-purple accent;
 * unmapped segments render amber (attention), never a new accent.
 */
import { cn } from '@/lib/utils';

export type AnatomyTone = 'default' | 'dynamic' | 'unmapped';

export interface AnatomySegment {
  /** Stable key used for hover-highlight matching (e.g. 'type' | 'size' | 'colour' | 'design'). */
  key: string;
  text: string;
  label: string;
  tone?: AnatomyTone;
}

interface SkuAnatomyProps {
  segments: AnatomySegment[];
  /** Segment `key` to highlight (hover on a mapping card). */
  highlight?: string | null;
  size?: 'md' | 'lg';
}

export function SkuAnatomy({ segments, highlight = null, size = 'md' }: SkuAnatomyProps) {
  return (
    <div className="flex flex-wrap items-stretch gap-x-1 gap-y-1.5">
      {segments.map((seg, i) => {
        const isHighlighted = highlight != null && seg.key === highlight;
        const tone = seg.tone ?? 'default';
        return (
          <div key={seg.key} className="flex items-stretch">
            {i > 0 && (
              <span aria-hidden className="self-center px-0.5 font-mono text-muted-foreground/70">
                -
              </span>
            )}
            <span
              className={cn(
                'flex flex-col rounded-md border px-2 py-1 transition-colors duration-200 motion-reduce:transition-none',
                size === 'lg' ? 'min-w-[2.5rem]' : 'min-w-[2rem]',
                tone === 'default' && 'border-border bg-muted/60',
                tone === 'dynamic' && 'border-threadheads-purple/30 bg-threadheads-purple/10',
                tone === 'unmapped' && 'border-amber-300 bg-amber-50',
                isHighlighted && 'border-threadheads-purple/40 bg-threadheads-purple/10 ring-1 ring-threadheads-purple/20',
              )}
            >
              <span
                className={cn(
                  'font-mono font-semibold tabular-nums',
                  size === 'lg' ? 'text-base' : 'text-sm',
                  tone === 'dynamic' && 'text-threadheads-purple',
                  tone === 'unmapped' && 'text-amber-700',
                )}
              >
                {seg.text}
              </span>
              <span
                className={cn(
                  'mt-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  tone === 'dynamic' || isHighlighted ? 'text-threadheads-purple' : 'text-muted-foreground/70',
                  tone === 'unmapped' && 'text-amber-600',
                )}
              >
                {seg.label}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
